import { clampPlayerPitch, normalizePlayerYaw, playerYawDifference, type PackedPlayer } from "@endclock/shared";

export const REMOTE_INTERPOLATION_DELAY_MS = 120;
export const REMOTE_MAX_EXTRAPOLATION_MS = 100;
export const REMOTE_SNAPSHOT_LIMIT = 24;
export const REMOTE_TELEPORT_DISTANCE = 6;
interface Sample { readonly player: PackedPlayer; readonly at: number; readonly tick: number }

/** A timeline, not a packet-arrival lerp. Discrete state switches at sample time. */
export class RemotePlayerInterpolation {
  private readonly samples: Sample[] = [];
  private generation = -1;
  rejected = 0;
  resets = 0;
  constructor(readonly delayMs = REMOTE_INTERPOLATION_DELAY_MS, readonly extrapolationMs = REMOTE_MAX_EXTRAPOLATION_MS) {}
  get size(): number { return this.samples.length; }
  clear(): void { this.samples.length = 0; this.generation = -1; }

  push(player: readonly [...PackedPlayer, ...unknown[]], at: number, tick: number, generation = 0): boolean {
    if (!Number.isFinite(at) || !Number.isSafeInteger(tick) || tick < 0) { this.rejected += 1; return false; }
    const last = this.samples.at(-1);
    if (generation === this.generation && last && (tick <= last.tick || at <= last.at)) { this.rejected += 1; return false; }
    if (generation !== this.generation || (last && (player[21] !== last.player[21]
      || Math.hypot(player[1] - last.player[1], player[2] - last.player[2], player[3] - last.player[3]) > REMOTE_TELEPORT_DISTANCE))) {
      this.samples.length = 0; this.resets += 1;
    }
    this.generation = generation;
    this.samples.push({ player: player.slice(0, 22) as unknown as PackedPlayer, at, tick });
    while (this.samples.length > REMOTE_SNAPSHOT_LIMIT) this.samples.shift();
    return true;
  }

  sample(serverNow: number): PackedPlayer | null {
    if (!this.samples.length || !Number.isFinite(serverNow)) return null;
    const at = serverNow - this.delayMs;
    const first = this.samples[0]!;
    if (at <= first.at) return first.player;
    for (let index = 1; index < this.samples.length; index += 1) {
      const previous = this.samples[index - 1]!, next = this.samples[index]!;
      if (at <= next.at) return interpolatePlayer(previous.player, next.player, (at - previous.at) / (next.at - previous.at));
    }
    const latest = this.samples.at(-1)!;
    const seconds = Math.min(this.extrapolationMs, Math.max(0, at - latest.at)) / 1000;
    const player = [...latest.player];
    player[1] = latest.player[1] + latest.player[8] * seconds;
    player[2] = latest.player[2] + latest.player[9] * seconds;
    player[3] = latest.player[3] + latest.player[10] * seconds;
    return player as unknown as PackedPlayer;
  }
}

export function interpolatePlayer(from: PackedPlayer, to: PackedPlayer, amount: number): PackedPlayer {
  const alpha = Math.max(0, Math.min(1, amount));
  const result = [...(alpha >= 1 ? to : from)];
  for (const index of [1, 2, 3, 7, 8, 9, 10] as const) result[index] = from[index] + (to[index] - from[index]) * alpha;
  result[4] = normalizePlayerYaw(from[4] + playerYawDifference(from[4], to[4]) * alpha);
  result[11] = normalizePlayerYaw(from[11] + playerYawDifference(from[11], to[11]) * alpha);
  result[12] = clampPlayerPitch(from[12] + (to[12] - from[12]) * alpha);
  return result as unknown as PackedPlayer;
}
